/**
 * /operations/transactions/loan/new — Loan HR-initiate form
 * (M30.5.3). Same pattern as Leave with loan-specific fields
 * (product_code + amount + tenure + purpose).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmployeePicker } from "../../components/ui/EmployeePicker";
import { TransactionFormShell } from "../../components/hr_operations";
import { api, type EmployeePickerOption } from "../../lib/api";
import { useHrActor } from "../../lib/use-hr-actor";
import { useTransactionAssist } from "../../lib/use-transaction-assist";

export function LoanHrInitiatePage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<EmployeePickerOption | null>(
    null,
  );
  const [reasonCode, setReasonCode] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [docUrl, setDocUrl] = useState<string>("");
  const [productCode, setProductCode] = useState<string>("PERSONAL");
  const [amount, setAmount] = useState<string>("");
  const [tenure, setTenure] = useState<string>("12");
  const [purpose, setPurpose] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reasonCodes = useQuery({
    queryKey: ["reason-codes", "loan"],
    queryFn: () => api.listReasonCodes("loan"),
    staleTime: 5 * 60_000,
  });

  // Actor identity from Clerk (audit fix C1 — commandment 39).
  const actor = useHrActor();

  const enabled =
    actor.isReady &&
    !!employee &&
    !!reasonCode &&
    !!productCode &&
    !!amount &&
    !!tenure;
  const body = {
    actor_user_id: actor.actorUserId,
    actor_role: actor.actorRole,
    target_employee_id: employee?.id ?? "",
    reason_code: reasonCode,
    reason_explanation: explanation || null,
    supporting_document_url: docUrl || null,
    loan_product_code: productCode,
    requested_amount: amount || "0",
    requested_tenure_months: Number(tenure || 0),
    purpose: purpose || null,
    disbursement_date: null,
  };
  const {
    result,
    isLoading,
    error,
    acknowledgedCodes,
    acknowledge,
  } = useTransactionAssist({
    body,
    enabled,
    callAssist: (b) => api.loanHrInitiateAssist(b),
  });

  async function submit() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.loanHrInitiateCommit({
        ...body,
        ai_findings_acknowledged: Array.from(acknowledgedCodes),
      });
      setSubmitSuccess(true);
      setTimeout(
        () => navigate("/operations/payroll-candidates"),
        1500,
      );
    } catch (e) {
      setSubmitError(String(e));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <TransactionFormShell
      transactionType="loan"
      title={t("operations.loan_form_title")}
      assist={result}
      isAssistLoading={isLoading}
      assistError={error}
      acknowledgedCodes={acknowledgedCodes}
      onAcknowledge={acknowledge}
      onAcceptSuggestion={(field, value) => {
        if (field === "reason_explanation" && typeof value === "string") {
          setExplanation(value);
        }
        if (
          field === "requested_tenure_months" &&
          typeof value === "number"
        ) {
          setTenure(String(value));
        }
      }}
      onSubmit={submit}
      isSubmitting={isSubmitting || !actor.isReady}
      submitSuccess={submitSuccess}
      submitError={submitError}
    >
      <Field label={t("operations.form_employee_label")}>
        <EmployeePicker
          value={employee?.id ?? null}
          onChange={setEmployee}
          selected={employee}
        />
      </Field>

      <Field label={t("operations.form_reason_code_label")}>
        <select
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          <option value="">—</option>
          {reasonCodes.data?.codes.map((c) => (
            <option key={c.code} value={c.code}>
              {isAr ? c.display_ar : c.display_en}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t("operations.loan_form_product_code")}>
        <input
          type="text"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={productCode}
          onChange={(e) => setProductCode(e.target.value)}
          placeholder="PERSONAL"
          dir="ltr"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("operations.loan_form_amount")}>
          <input
            type="number"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min={0}
            step="0.01"
            dir="ltr"
          />
        </Field>
        <Field label={t("operations.loan_form_tenure_months")}>
          <input
            type="number"
            className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
            value={tenure}
            onChange={(e) => setTenure(e.target.value)}
            min={1}
            dir="ltr"
          />
        </Field>
      </div>

      <Field label={t("operations.loan_form_purpose")}>
        <input
          type="text"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
        />
      </Field>

      <Field label={t("operations.form_reason_explanation_label")}>
        <textarea
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          rows={3}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
        />
      </Field>

      <Field label={t("operations.form_supporting_doc_label")}>
        <input
          type="url"
          className="w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm text-text-primary"
          value={docUrl}
          onChange={(e) => setDocUrl(e.target.value)}
          placeholder={t("operations.form_supporting_doc_placeholder")}
          dir="ltr"
        />
      </Field>
    </TransactionFormShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
